import { ApiEndpoint, ApiGroup } from './types';
import { API_PORTAL_TRY_IT_OUT_PREFIXES } from './constants';

/*
 * Formats a catalog timestamp as a short date, or "—" when absent/unparsable.
 */
export function formatCatalogDate(dateStr?: string): string {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export interface CatalogFilterCriteria {
    searchQuery?: string;
    selectedGroup?: string | null;
    selectedMethods?: string[];
    selectedVersions?: string[];
    selectedStatuses?: string[];
}

/*
 * Single shared filter over catalog endpoints — search/group/method/version/status.
 * The public Catalog page uses every criterion; the Settings > Endpoints manager only
 * ever passes searchQuery/selectedGroup/selectedMethods, leaving the rest undefined.
 */
export function filterCatalogEndpoints(
    endpoints: ApiEndpoint[],
    criteria: CatalogFilterCriteria,
): ApiEndpoint[] {
    const { searchQuery, selectedGroup, selectedMethods, selectedVersions, selectedStatuses } = criteria;

    return endpoints.filter((endpoint) => {
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesSearch =
                endpoint.name.toLowerCase().includes(query) ||
                endpoint.description.toLowerCase().includes(query) ||
                endpoint.path.toLowerCase().includes(query) ||
                endpoint.tags.some((tag) => tag.toLowerCase().includes(query));
            if (!matchesSearch) return false;
        }

        if (selectedGroup && String(endpoint.group).toLowerCase() !== String(selectedGroup).toLowerCase()) {
            return false;
        }

        if (selectedMethods && selectedMethods.length > 0 && !selectedMethods.includes(endpoint.method)) {
            return false;
        }

        if (selectedVersions && selectedVersions.length > 0 && !selectedVersions.includes(endpoint.version)) {
            return false;
        }

        if (
            selectedStatuses &&
            selectedStatuses.length > 0 &&
            endpoint.status &&
            !selectedStatuses.includes(endpoint.status)
        ) {
            return false;
        }

        return true;
    });
}

/*
 * Buckets endpoints by their group key, for accordion sections and per-group counts.
 */
export function groupEndpointsByGroup(endpoints: ApiEndpoint[]): Record<string, ApiEndpoint[]> {
    const grouped: Record<string, ApiEndpoint[]> = {};
    endpoints.forEach((endpoint) => {
        if (!grouped[endpoint.group]) grouped[endpoint.group] = [];
        grouped[endpoint.group].push(endpoint);
    });
    return grouped;
}

/*
 * Counts endpoints per group key, for group-card endpoint counts and delete warnings.
 */
export function countEndpointsByGroup(endpoints: ApiEndpoint[]): Record<string, number> {
    return endpoints.reduce((acc: Record<string, number>, endpoint) => {
        acc[endpoint.group] = (acc[endpoint.group] || 0) + 1;
        return acc;
    }, {});
}

/*
 * Single source of truth reconciling ApiGroup.groupKey / ApiGroup.id / ApiEndpoint.group,
 * which the backend does not consistently key the same way. Every place that needs to match
 * a group against an endpoint, a filter value, or a form selection goes through one of these
 * three functions rather than comparing the raw fields directly.
 */

/*
 * The value to filter endpoints by for a given group (aligns with endpoint.group).
 */
export function resolveGroupFilterKey(group: ApiGroup): string {
    return group.groupKey ?? String(group.id);
}

/*
 * Resolves a group create/edit form selection (a group id or an already-typed key) to the
 * catalog groupKey the backend expects on an endpoint's `group` field.
 */
export function resolveGroupKeyForBackend(groupIdOrKey: string, groups: ApiGroup[]): string {
    if (Number.isNaN(Number(groupIdOrKey))) {
        return groupIdOrKey;
    }

    const group = groups.find((g) => String(g.id) === String(groupIdOrKey));
    if (group) {
        return group.groupKey ?? group.name.toLowerCase().replace(/\s+/g, '-');
    }

    return groupIdOrKey;
}

/*
 * Resolves the group id to bind to an edit form's Select when an endpoint's stored `group`
 * string needs to be matched back to one of the known groups (by id, groupKey, or name).
 */
export function resolveGroupIdForEndpoint(endpointGroup: string, groups: ApiGroup[]): string {
    const endpointGroupLower = String(endpointGroup || '').toLowerCase();
    const matchingGroup = groups.find((g) => {
        const groupId = String(g.id ?? '').toLowerCase();
        const groupKey = String(g.groupKey ?? '').toLowerCase();
        const groupName = String(g.name ?? '').toLowerCase();
        return groupId === endpointGroupLower || groupKey === endpointGroupLower || groupName === endpointGroupLower;
    });

    return matchingGroup ? String(matchingGroup.id) : endpointGroup;
}

/*
 * The `?group=` value for a group's catalog deep link — resolves from nested endpoints, then
 * the flat all-endpoints list, then a name slug, since /groups summaries don't always carry
 * a stable groupKey.
 */
export function resolveCatalogGroupParam(group: ApiGroup, allEndpoints?: ApiEndpoint[]): string {
    if (group.groupKey && group.groupKey.trim() !== '') return group.groupKey.trim();
    if (typeof group.id === 'string' && group.id.trim() !== '' && !/^\d+$/.test(group.id.trim())) {
        return group.id.trim();
    }

    const nested = group.endpoints ?? [];
    const withGroup = nested.find((e) => e.group != null && String(e.group).trim() !== '');
    if (withGroup) return String(withGroup.group).trim();

    if (allEndpoints?.length) {
        const byIdMatchesGroupKey = allEndpoints.find(
            (e) => String(e.group).trim() !== '' && String(e.group).trim() === String(group.id).trim(),
        );
        if (byIdMatchesGroupKey) return String(byIdMatchesGroupKey.group).trim();
    }

    const slug = group.name
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
    return slug || group.name.trim() || 'group';
}

/*
 * Maps a raw group key to a human-readable display name and icon when the API returns
 * endpoints without rich group metadata.
 */
function getFallbackGroupDisplay(groupKey: string): { name: string; icon: string } {
    if (groupKey === 'auth') return { name: 'Authentication', icon: 'shield' };
    if (groupKey === 'upload') return { name: 'File Upload', icon: 'upload' };
    if (groupKey === 'workflow') return { name: 'Workflows & Jobs', icon: 'workflow' };
    return { name: groupKey.charAt(0).toUpperCase() + groupKey.slice(1), icon: 'workflow' };
}

/*
 * Synthesizes ApiGroup objects from the flat endpoint list when the groups API returns none
 * (or none with populated endpoints) — used by the Portal overview as a fallback data source.
 */
export function buildFallbackGroupsFromEndpoints(allEndpoints?: ApiEndpoint[]): ApiGroup[] {
    if (!allEndpoints || allEndpoints.length === 0) return [];

    const grouped = groupEndpointsByGroup(allEndpoints);
    return Object.entries(grouped).map(([groupKey, endpoints]) => {
        const display = getFallbackGroupDisplay(groupKey);
        return {
            id: groupKey,
            name: display.name,
            description: `APIs for ${display.name.toLowerCase()}`,
            icon: display.icon,
            endpoints,
        };
    });
}

export type JsonTokenType = 'key' | 'string' | 'number' | 'boolean' | 'null' | 'punctuation' | 'plain';

export const JSON_TOKEN_COLORS: Record<JsonTokenType, string> = {
    key: '#60a5fa',
    string: '#fb923c',
    number: '#34d399',
    boolean: '#c084fc',
    null: '#c084fc',
    punctuation: '#94a3b8',
    plain: '#e2e8f0',
};

/*
 * Splits one line of JSON text into typed tokens for the response/body syntax highlighter.
 */
export function tokenizeJsonLine(line: string): { text: string; type: JsonTokenType }[] {
    const tokens: { text: string; type: JsonTokenType }[] = [];
    const re =
        /("(?:[^"\\]|\\.)*")(\s*:)|("(?:[^"\\]|\\.)*")|(true|false)|(null)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],:])|(\s+|[^\s"{}[\],:]+)/g;
    let match: RegExpExecArray | null;
    while ((match = re.exec(line)) !== null) {
        if (match[1] && match[2] !== undefined) {
            tokens.push({ text: match[1], type: 'key' });
            tokens.push({ text: match[2], type: 'punctuation' });
        } else if (match[3]) {
            tokens.push({ text: match[3], type: 'string' });
        } else if (match[4]) {
            tokens.push({ text: match[4], type: 'boolean' });
        } else if (match[5]) {
            tokens.push({ text: match[5], type: 'null' });
        } else if (match[6]) {
            tokens.push({ text: match[6], type: 'number' });
        } else if (match[7]) {
            tokens.push({ text: match[7], type: 'punctuation' });
        } else {
            tokens.push({ text: match[0], type: 'plain' });
        }
    }
    return tokens;
}

/*
 * Badge color and label for an HTTP status code, shared by response docs and Try It Out.
 */
export function getStatusMeta(status: number): { color: string; background: string; label: string } {
    if (status >= 200 && status < 300) return { color: '#52c41a', background: '#0a3d24', label: 'Success' };
    if (status >= 400 && status < 500) return { color: '#fcad01', background: '#4a3300', label: 'Client Error' };
    if (status >= 500) return { color: '#ff4d4f', background: '#4a1111', label: 'Server Error' };
    return { color: '#1890ff', background: '#0d3049', label: 'Info' };
}

/*
 * Builds the Try It Out request URL: resolves the backend service prefix from the endpoint's
 * `endpointKey`, substitutes `{param}` path params, then appends the query string.
 */
export function buildRequestUrl(
    path: string,
    endpointKey: string,
    pathParams: Record<string, string>,
    queryParams: Record<string, string>,
): string {
    let url = path;

    if (endpointKey.startsWith('instance')) {
        url = `${API_PORTAL_TRY_IT_OUT_PREFIXES.asset}${url}`;
    } else if (endpointKey.startsWith('workflow-create')) {
        url = `${API_PORTAL_TRY_IT_OUT_PREFIXES.airflowCreate}${url}`;
    } else if (endpointKey.startsWith('workflow-execute-job') || endpointKey.startsWith('workflow-get-job-status')) {
        url = `${API_PORTAL_TRY_IT_OUT_PREFIXES.databricksJob}${url}`;
    } else if (endpointKey.startsWith('neo4j') || endpointKey.startsWith('edge-panel')) {
        url = `${API_PORTAL_TRY_IT_OUT_PREFIXES.databrowserPanel}${url}`;
    } else if (endpointKey.startsWith('workflow-get')) {
        url = `${API_PORTAL_TRY_IT_OUT_PREFIXES.platformGet}${url}`;
    }

    Object.entries(pathParams).forEach(([key, value]) => {
        url = url.replace(`{${key}}`, encodeURIComponent(value));
    });

    const queryString = Object.entries(queryParams)
        .filter(([, value]) => value)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

    return queryString ? `${url}?${queryString}` : url;
}

/*
 * Seeds Try It Out's path/query/header param inputs from an endpoint's documented examples.
 */
export function createInitialParams(
    parameters: ApiEndpoint['parameters'] | undefined,
    type: 'path' | 'query' | 'header',
): Record<string, string> {
    const initial: Record<string, string> = {};
    parameters?.[type]?.forEach((param) => {
        initial[param.name] = param.example || '';
    });
    return initial;
}
